/* @flow */
/* eslint-env es6,browser */

import { setLoggerGif, setErrorExcludeReporting } from './utils/logger';
import {
  extend,
  isString,
  isArray,
  isFunction,
  pickRandom
} from './utils/coreutils';

/**
 *
 * @module adconfig
 * @exports adconfig
 * @requires module:logger
 * @requires module:coreutils
 * @description Implements ad configuration functionality
 */

/**
 * @global
 * @typedef rotationConfig
 * @property {number} duration Ad rotation duration (in sec)
 * @property {number} durationEnd Duration till rotate ads (in sec)
 * @property {string} positions Ad positions to rotate
 * @property {string} exclude Exclude browser & os combination from rotation
 * @property {boolean} onlyActivePage Rotate only when the page is active(default:off)
 */

class _adconfig {
  _win: WindowProxy;

  _doc: Document;

  _pageInfo: pageInfo;

  _lastConfigLoadTime: number;

  _gPageInfo: pageInfo;

  _defConfig: adConfig;

  _config: adConfig;

  _configOverride: {};

  constructor() {
    this._win = window;
    this._doc = document;
    /**
     * @global
     * @external hbConfig
     * @typedef adConfig
     * @property {object} settings HB Settings
     * @property {rotationConfig} settings.adRotate Ad rotation config
     * @property {object.<string, hbConfig>} settings.headerBidding HB config map
     * @property {object.<string, posConfigInfo>} settings.posMap Position map
     * @property {object.<string, module:_adserverbase._perAdServerConfig>} settings.adServer Ad server map
     * @property {string} settings.loggerGif Logger path(gif image)
     * @property {number} settings.configExpirtySec Config cache duration(in sec)
     * @property {number} settings.loadingDelay Delay
     */
    this._defConfig = {
      settings: {
        cookieManager: {
          checkInterval: 0,
          reportingLength: 4000,
          cleanupLength: 5000,
          forceCleanupLength: 0,
          dndCookies: [],
          cleanupCookies: [],
          minCleanupCookieLength: 100
        },
        adRotate: {
          exclude: '',
          positions: '',
          delay: 0,
          duration: 60,
          durationEnd: -1,
          onlyActivePage: false
        },
        headerBidding: {
          bidders: [],
          bidders_dialup: [],
          biddersOverride: [],
          shuffleBidders: false,
          timeout: 3000,
          userSync: {},
          granularity: 'dense'
        },
        loggerGif: '//static.uolcontent.com/images/spacer.gif',
        errExcludeReporting: '',
        configExpirySec: 20 * 60,
        creatives: {},
        posMap: {},
        adServer: {},
        il: {
          obj: 'I12C',
          key: 'Morph'
        },
        adloading: {
          delay: 200,
          scheduler: '',
          sandbox: ''
        },
        userIds: {
          ids: []
        },
        cvFilter: []
      }
    };
    this._config = extend(true, {}, this._defConfig);
    this._pageInfo = {
      posList: [],
      name: ''
    };
    this._lastConfigLoadTime = 0;
    this._gPageInfo = {
      posList: [],
      name: ''
    };
    this._configOverride = {};
  }

  /**
   * Initialize the object
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: WindowProxy, d: Document, g: globalParam) {
    this._win = w;
    this._doc = d;
    this._configOverride = g.adConfig.override || {};
    this._gPageInfo = g.pageInfo;
    setLoggerGif(this._config.settings.loggerGif);
    setErrorExcludeReporting(this._config.settings.errExcludeReporting);
  }

  /**
   * Sets pageName & posList. posList is always cached as Array.
   *
   * @param {string} [pageName] - Pagename
   * @param {string|Array<string>} [posList] - Position list
   * @returns {undefined}
   */
  setPageInfo(pageName: ?string = '', posList: ?(string | Array<string>) = '') {
    const list = posList || '';
    this._pageInfo.name = pageName || '';
    if (isString(list) && list.length) {
      // $FlowFixMe
      this._pageInfo.posList = list.split(',');
    } else if (isArray(list) && list.length) {
      this._pageInfo.posList = list;
    }
  }

  /**
   * @global
   * @callback getPageNameCB
   * @param {object} context
   */
  /**
   * Getter function for pagename
   *
   * @param {string|getPageNameCB} s - input can be string or function
   * @returns {string} - Page name
   */
  getPageName(s: ?(string | (WindowProxy => string)) = ''): string {
    // flowlint-next-line sketchy-null-string:off
    const pageName = s || this._pageInfo.name || this._gPageInfo.name;

    if (isString(pageName)) {
      // $FlowFixMe
      return pageName;
    }
    if (isFunction(pageName)) {
      // $FlowFixMe
      return pageName.call(this._win);
    }
    return '';
  }

  /**
   * @global
   * @callback getPagePositionsCB
   * @param {object} context
   * @param {string} page - pagename
   */
  /**
   * Getter function for ad positions for the current page
   *
   * @param {string|Array<string>|getPagePositionsCB} [v] - list of ad positions
   * @param {string|getPageNameCB} pageName - current page name
   * @param {boolean} [bRotation=false] - whether called during ad rotation
   * @returns {Array<string>} - Ad positions
   */
  getPagePositions(
    v: ?(string | Array<string> | ((WindowProxy, string) => string)),
    pageName: string | (WindowProxy => string) = '',
    bRotation: boolean = false
  ): Array<string> {
    let posList = v;
    // flowlint-next-line sketchy-null-string:off
    if (!posList && this._pageInfo.posList.length > 0) {
      posList = this._pageInfo.posList;
    }

    // flowlint-next-line sketchy-null-string:off
    if (!posList) {
      posList = this._gPageInfo.posList;
    }

    if (isFunction(posList)) {
      // $FlowFixMe
      posList = posList.call(this._win, this.getPageName(pageName), bRotation);
    }
    posList = posList || '';
    // get and return array
    if (isString(posList) && posList.length) {
      // $FlowFixMe
      posList = posList.split(',');
    } else if (!isArray(posList)) {
      posList = [];
    }
    // $FlowFixMe
    return posList;
  }

  /**
   * processConfigData - take cares of updating caches and propagating config change
   *
   * @param {adConfig} data - AdConfig data
   * @returns {undefined}
   * @access private
   */

  processConfigData(data: adConfig) {
    // update in-memory cache
    this._config = extend(
      true,
      {},
      this._defConfig,
      data,
      this._configOverride
    );

    if (this._config.settings.loggerGif) {
      setLoggerGif(this._config.settings.loggerGif);
    }
    setErrorExcludeReporting(this._config.settings.errExcludeReporting);
  }

  /**
   * getter for config
   *
   * @returns {adConfig} - Ad Config
   */
  get(): adConfig {
    return this._config;
  }

  /**
   * getter for creatives
   *
   * @param {string} pos - Ad Position
   * @returns {string} - Creatives string
   */
  getCreative(pos: string = ''): string {
    let ret = '';
    if (!pos.length) {
      return ret;
    }
    const posInfo = this.getPosSettings(pos);
    const creativeKey = posInfo.creative || '';
    if (isString(creativeKey) && creativeKey.length === 0) {
      // not a valid creative key configured here
      return ret;
    }

    const creativeKeys: Array<string> = isString(creativeKey)
      ? // $FlowFixMe
        [creativeKey]
      : // $FlowFixMe
        creativeKey;
    const creativeLen = creativeKeys.length;
    if (creativeLen === 0) {
      return ret;
    }

    const creatives = this._config.settings.creatives || {};
    ret = creatives[pickRandom(creativeKeys)] || '';
    return ret;
  }

  /**
   * @global
   * @callback contentHandlerCB
   * @param {string} page - pagename
   * @param {string} pos - ad position
   * @param {string} data - ad content
   */

  /**
   * @global
   * @typedef posConfigInfo
   * @property {string} as Ad server name
   * @property {boolean} [aFold=false] Above Fold/not
   * @property {string} [auid] AdUnit ID
   * @property {number} width Width
   * @property {number} height Height
   * @property {boolean} [iframe=true] Should be loaded in iframe?
   * @property {boolean} [gscript=false] Should execute the creative on top window. Support CSS/JS injection. (Deprecated)
   * @property {boolean} [dynHeight=false] Dynamically expand height (Deprecated)
   * @property {number}	[MarginHeight] Margin Height (Deprecated)
   * @property {contentHandlerCB}	callback Callback after fetching ad(deprecated)
   */
  /**
   * getter for position specific config
   *
   * @param {string} s - position name
   * @returns {posConfigInfo} - Position config
   */
  getPosSettings(s: string): posConfigInfo {
    // by default we will return 1*1 adSettings
    const _def: posConfigInfo = {
      width: 1,
      height: 1,
      as: '',
      loadingMethod: 1,
      minPercentRotationViewport: 0,
      onlyActivePage: false,
      adUnitPath: '',
      aFold: false
    };
    let ret: posConfigInfo = {};
    if (s.length > 0 && this._config.settings.posMap[s]) {
      ret = this._config.settings.posMap[s];
    }
    ret = extend(true, _def, ret);
    return ret;
  }

  /**
   * getter for above the fold or not
   *
   * @param {string} pos - Ad Position
   * @returns {boolean} - is above fold?
   */
  isAboveFold(pos: string = ''): boolean {
    // By default all positions are below fold
    let aFold: boolean = false;
    if (!pos.length) {
      return aFold;
    }
    aFold = Boolean(this.getPosSettings(pos).aFold);
    return aFold;
  }

  /**
   * Getter for GPID value
   * Uses GAM adUnitPath if available
   * @param {string} pos - Ad position
   * @returns {String} - generated GPID value
   */
  getGPID(pos: string = ''): string {
    let ret = '';
    if (pos.length <= 0) {
      return ret;
    }
    const { adUnitPath = '' } = this.getPosSettings(pos);
    ret = adUnitPath.length > 0 ? `${adUnitPath}#${pos}` : pos;
    return ret;
  }
}

// export singleton object as default for this module
const ac: _adconfig = new _adconfig();
export default ac;
